"use client";

import { useEffect, useState } from "react";
import {
  isStaleAssetError,
  parseReloadGuard,
  shouldAutoReload,
  STALE_RELOAD_GUARD_KEY
} from "@/lib/staleAssets";

/**
 * Error boundary GLOBAL (menggantikan root layout saat error lolos ke atas).
 *
 * Dua tugas:
 * 1. AUTO-RELOAD SEKALI saat error bercorak "aset build basi" (ChunkLoadError /
 *    CSS chunk 404 setelah deploy di Hostinger — HTML lama merujuk chunk build
 *    lama yang sudah tak ada di server). Reload mengambil HTML build terbaru →
 *    sembuh sendiri tanpa pengguna harus menekan Reload di error page browser.
 *    Guard sessionStorage (60 dtk) mencegah loop reload bila penyebabnya lain.
 * 2. Menampilkan layar error BERMEREK (bukan layar error browser/Next default)
 *    dengan tombol Muat Ulang — gaya cermin halaman /offline.html.
 *
 * WAJIB mandiri: render <html><body> sendiri + inline style (tanpa Tailwind /
 * CSS _next) karena CSS global bisa jadi justru aset yang gagal dimuat.
 */
// Warna di halaman ini NETRAL (ink/muted), bukan warna brand — sama alasannya
// dengan /offline.html: layar ini justru tampil ketika CSS/JS gagal dimuat, jadi ia
// tak bisa membaca Warna Tema toko. Lihat docs/audit-warna-tema.md §P10.
export default function GlobalError({
  error
}: {
  error: Error & { digest?: string };
}) {
  const [autoReloading, setAutoReloading] = useState(false);

  useEffect(() => {
    if (!isStaleAssetError(error)) return;
    let last: number | null = null;
    try {
      last = parseReloadGuard(window.sessionStorage.getItem(STALE_RELOAD_GUARD_KEY));
    } catch {
      /* sessionStorage bisa terblokir (mode privat ketat) — lanjut tanpa guard */
    }
    if (!shouldAutoReload(last, Date.now())) return;
    try {
      window.sessionStorage.setItem(STALE_RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      /* tanpa guard tersimpan pun reload tetap satu kali per mount */
    }
    setAutoReloading(true);
    window.location.reload();
  }, [error]);

  return (
    <html lang="id">
      <body style={{ margin: 0 }}>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background: "#FFF9FA",
            color: "#222126",
            fontFamily:
              "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif"
          }}
        >
          <div style={{ maxWidth: "360px", textAlign: "center" }}>
            <div
              style={{
                width: "72px",
                height: "72px",
                margin: "0 auto 20px",
                borderRadius: "20px",
                background: "#222126",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "36px",
                fontWeight: 800
              }}
              aria-hidden="true"
            >
              G
            </div>
            <h1 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 8px" }}>
              {autoReloading ? "Memuat ulang…" : "Halaman gagal dimuat"}
            </h1>
            <p
              style={{
                fontSize: "14px",
                lineHeight: 1.6,
                color: "#706B75",
                margin: "0 0 20px"
              }}
            >
              {autoReloading
                ? "Aplikasi baru saja diperbarui. Halaman dimuat ulang otomatis."
                : "Terjadi kendala saat memuat halaman — biasanya karena aplikasi baru saja diperbarui atau koneksi terputus sesaat. Muat ulang untuk melanjutkan."}
            </p>
            {!autoReloading && (
              <div>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    display: "inline-block",
                    padding: "12px 22px",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    background: "#222126",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600
                  }}
                >
                  Muat Ulang
                </button>
                <div style={{ marginTop: "12px" }}>
                  <a
                    href="/"
                    style={{ fontSize: "13px", color: "#706B75", textDecoration: "none" }}
                  >
                    Kembali ke Beranda
                  </a>
                </div>
              </div>
            )}
            {error?.digest && (
              <p style={{ fontSize: "11px", color: "#B9B3BF", margin: "16px 0 0" }}>
                Kode: {error.digest}
              </p>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
